import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type { AuthTokens } from '@/types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
const CSRF_COOKIE_NAME = 'csrf-token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

class ApiClient {
  private client: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
      withCredentials: true, // Required for CSRF cookies
    });

    this.setupInterceptors();
  }

  /**
   * Get CSRF token from cookie
   * The cookie is set by the server on login and is readable by JavaScript
   */
  private getCSRFToken(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === CSRF_COOKIE_NAME && value) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  private setupInterceptors() {
    // Request interceptor - add auth token and CSRF token
    this.client.interceptors.request.use(
      (config) => {
        // Add Authorization header
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add CSRF token header for state-changing requests
        const method = config.method?.toUpperCase();
        if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          const csrfToken = this.getCSRFToken();
          if (csrfToken) {
            config.headers[CSRF_HEADER_NAME] = csrfToken;
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        const requestUrl: string = originalRequest?.url || '';
        const isAuthEndpoint =
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/registration-login') ||
          requestUrl.includes('/auth/verify-2fa') ||
          requestUrl.includes('/auth/register') ||
          requestUrl.includes('/auth/refresh');

        // Handle 401 errors (token expired)
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;

          try {
            const newAccessToken = await this.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout user
            this.clearTokens();
            window.location.href = '/auth';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private setTokens(tokens: AuthTokens): void {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('tokenExpiry', String(Date.now() + tokens.expiresIn * 1000));
  }

  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('tokenExpiry');
  }

  private async refreshAccessToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      const response = await axios.post<AuthTokens>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );

      const tokens = response.data;
      this.setTokens(tokens);
      this.refreshTokenPromise = null;
      return tokens.accessToken;
    })();

    return this.refreshTokenPromise;
  }

  // Public methods

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Download a file from an admin export endpoint. Returns the Blob plus the
   * server-suggested filename (parsed from Content-Disposition) so the caller
   * can hand it straight to the browser save flow.
   */
  async downloadFile(url: string, config?: AxiosRequestConfig): Promise<{ blob: Blob; filename: string | null }> {
    const response = await this.client.get<Blob>(url, { ...config, responseType: 'blob' });
    const disposition = response.headers['content-disposition'] as string | undefined;
    let filename: string | null = null;
    if (disposition) {
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      if (match && match[1]) filename = match[1];
    }
    return { blob: response.data, filename };
  }

  async uploadFile<T>(url: string, file: File, fieldName = 'file'): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const response = await this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  // Auth methods
  setAuthTokens(tokens: AuthTokens): void {
    this.setTokens(tokens);
  }

  clearAuth(): void {
    this.clearTokens();
  }

  isAuthenticated(): boolean {
    // Token presence — not localStorage tokenExpiry — gates the boot path.
    // The 401 response interceptor refreshes a stale access token using the
    // refresh cookie, so a passed tokenExpiry must not force a sign-out at
    // app boot when the refresh session is still valid.
    return this.getAccessToken() !== null;
  }
}

export const api = new ApiClient();
